import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { startDemo } from '../lib/demo-customer.js';
import { BRAND, NETCOM_LOCKUP, PRODUCT_ACCENTS } from '../config/brand.js';
import { useUserMode } from '../lib/user-mode.jsx';
import { getSession, getAccountNumber } from '../lib/auth.js';
import { getSimAccess } from '../lib/sim-api.js';
import { FEATURES } from '../config/features.js';
import { getTerminals, getServiceLines } from '../lib/starlink-api.js';
import { getImpersonation } from '../lib/impersonation.js';

// ============================================================
// Sidebar — JARVIS-grade premium nav
// ============================================================
// Visual hierarchy upgrades:
//   - Brand block: bigger logo (40×40), real Inter SemiBold,
//     mono uppercase subtitle, hairline separator
//   - Section labels: mono uppercase, refined letter-spacing
//     (0.18em for muted sections, 0.14em for product sections
//     so Starlink/SIM read just barely louder than plumbing)
//   - Nav rows: 13px Inter, slightly looser line-height, full
//     touch-friendly padding. Active state = chip fill
//     (`bg-white/[0.07]`) + left accent bar + white text. NOT
//     a faint underline.
//   - Footer: company mark + ASP disclosure in mono uppercase
//     so it reads as compliance metadata, not marketing.

const SECTIONS = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: IconHome, mode: 'both' },
    ],
  },
  {
    label: 'Starlink',
    products: ['starlink'],
    items: [
      { to: '/network', label: 'Network Map', icon: IconMap, mode: 'both' },
      { to: '/terminals', label: 'Dishes', icon: IconDevice, mode: 'both', badgeKey: 'dishes' },
      { to: '/service-lines', label: 'Service Lines', icon: IconLink, mode: 'both', badgeKey: 'serviceLines' },
      { to: '/groups', label: 'Groups & Tags', icon: IconFolder, mode: 'pro', flag: 'FLEET_GROUPS' },
      { to: '/add-kit', label: 'Add Existing Dish', icon: IconPlus, mode: 'both' },
    ],
  },
  {
    label: 'Netcom Connect SIM',
    products: ['sim'],
    items: [
      { to: '/sims', label: 'SIM Cards', icon: IconSim, mode: 'both' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/alerts', label: 'Alerts', icon: IconBell, mode: 'both' },
      { to: '/thresholds', label: 'Alert Rules', icon: IconSliders, mode: 'pro', flag: 'ALERT_RULES' },
      { to: '/maintenance', label: 'Maintenance', icon: IconWrench, mode: 'pro', flag: 'MAINTENANCE_WINDOWS' },
      { to: '/sla', label: 'SLA Performance', icon: IconShield, mode: 'pro', flag: 'ENTERPRISE_ANALYTICS' },
      { to: '/trends', label: 'Trends & Reports', icon: IconChart, mode: 'pro', flag: 'ENTERPRISE_ANALYTICS' },
      { to: '/firmware', label: 'Firmware', icon: IconChip, mode: 'pro', flag: 'FIRMWARE_INTELLIGENCE' },
      { to: '/audit', label: 'Audit Log', icon: IconList, mode: 'pro' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/billing', label: 'Billing', icon: IconCard, mode: 'both' },
      { to: '/reports', label: 'Reports', icon: IconDoc, mode: 'pro', flag: 'ENTERPRISE_ANALYTICS' },
      { to: '/requests', label: 'Service Requests', icon: IconTicket, mode: 'both' },
      { to: '/api-access', label: 'API Access', icon: IconCode, mode: 'pro', flag: 'API_ACCESS' },
      { to: '/account/export', label: 'Export & delete', icon: IconDoc, mode: 'pro' },
      { to: '/support', label: 'Help & Support', icon: IconHelp, mode: 'both' },
    ],
  },
  {
    label: 'RF Surveys',
    products: ['field-services'],
    items: [
      { to: '/field-services/amazon-air',              label: 'Program Overview', icon: IconSignal, mode: 'both' },
      { to: '/field-services/amazon-air/sites',        label: 'Sites',          icon: IconMap,    mode: 'both' },
      { to: '/field-services/amazon-air/coverage',     label: 'Coverage',       icon: IconChart,  mode: 'both' },
      { to: '/field-services/amazon-air/work-orders',  label: 'Work Orders',    icon: IconWrench, mode: 'both' },
      { to: '/field-services/amazon-air/request',      label: 'Request Survey', icon: IconPlus,   mode: 'both' },
    ],
  },
  // Netcom-staff only. The `staffOnly` flag filters this section out
  // unless session.isNetcomAdmin is true. Backend Lambdas re-check the
  // netcom-admin group — the sidebar gate is UX only.
  {
    label: 'Netcom Admin',
    staffOnly: true,
    items: [
      { to: '/admin/leads',    label: 'Pending Signups',    icon: IconTicket, mode: 'both' },
      { to: '/admin/accounts', label: 'Customer Accounts',  icon: IconTeam,   mode: 'both' },
      { to: '/admin/dishes',   label: 'Master Dish Inventory', icon: IconDevice, mode: 'both' },
      { to: '/admin/starlink-credentials', label: 'Starlink Credentials', icon: IconShield, mode: 'both' },
      { to: '/admin/sims',     label: 'Master SIM Inventory',  icon: IconSim,   mode: 'both' },
      { to: '/admin/users',    label: 'User Management',    icon: IconTeam,   mode: 'both' },
      { to: '/admin/audit',    label: 'Cross-Tenant Audit', icon: IconDoc,    mode: 'both' },
      { to: '/admin/bulk-import', label: 'Bulk Import',     icon: IconDoc,    mode: 'both' },
      { to: '/admin/force-create', label: 'Force-Create Customer', icon: IconPlus, mode: 'both' },
      // Demo customer — UI-only sandbox. Clicking this routes into
      // the customer view backed by mock fixtures. Works even when
      // the Admin API is mid-deploy. Action wired in the click
      // handler below (special-cased on `demoAction: 'open'`).
      { to: '/admin/users',    label: 'Open Demo Customer', icon: IconDevice, mode: 'both', demoAction: 'open' },
    ],
  },
];

export default function Sidebar({ onNavigate }) {
  const { isSimple } = useUserMode();
  const session = getSession();
  const navigate = useNavigate();
  // While impersonating, product-gate on the CUSTOMER being viewed, not
  // the staff member's own (almost always empty) products claim — see
  // the fix note in lib/impersonation.js. getImpersonation() is a plain
  // synchronous sessionStorage read, re-evaluated on every render; the
  // badges effect below already re-renders this component on
  // 'netcom:impersonation-changed', which is what picks up a fresh value
  // here too (no separate state needed just for this).
  const impersonation = getImpersonation();
  const enabled = new Set(impersonation ? impersonation.products : (session?.products || []));
  // Any Starlink customer can SEE the Netcom Connect SIM section — as a live
  // preview showcase if they don't have the product yet (getSimAccess handles
  // the live/preview/none distinction; 'none' leaves the section hidden).
  if (getSimAccess() !== 'none') enabled.add('sim');
  const isStaff = !!session?.isNetcomAdmin;

  // Live fleet counts for the "Dishes" / "Service Lines" nav badges — so
  // the sidebar itself is glanceable, not just the Dashboard. Fetched
  // once per mount (Sidebar persists across page navigation inside
  // Layout) and re-fetched whenever impersonation changes (an admin
  // switching "View as customer" needs the badges for the NEW account,
  // not the stale ones from their own).
  const [badges, setBadges] = useState({ dishes: null, serviceLines: null });
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const accountNumber = getAccountNumber();
      if (!accountNumber) { setBadges({ dishes: null, serviceLines: null }); return; }
      const [terms, lines] = await Promise.all([
        getTerminals(accountNumber).catch(() => []),
        getServiceLines(accountNumber).catch(() => []),
      ]);
      if (cancelled) return;
      // Plain counts only — no per-device telemetry fetch just for a nav
      // badge (isOnline() needs each device's telemetry object as its 2nd
      // arg to be accurate; fetching that for every terminal on every
      // page load would be N extra API calls for a rough sidebar number).
      setBadges({
        dishes: terms.length ? String(terms.length) : null,
        serviceLines: lines.length ? String(lines.length) : null,
      });
    }
    load();
    window.addEventListener('netcom:impersonation-changed', load);
    return () => { cancelled = true; window.removeEventListener('netcom:impersonation-changed', load); };
  }, []);

  // Hook for the special "Open Demo Customer" sidebar entry. Doesn't
  // navigate via the NavLink — instead starts a UI-only demo session
  // and routes the admin to the customer dashboard.
  function handleDemoOpen(e) {
    e.preventDefault();
    startDemo();
    onNavigate?.();
    navigate('/', { replace: true });
  }
  const filteredSections = SECTIONS
    .filter((s) => !s.staffOnly || isStaff)
    .filter((s) => !s.products || s.products.some((p) => enabled.has(p)))
    .map((s) => ({
      ...s,
      items: s.items
        .filter((it) => !it.flag || FEATURES[it.flag])
        .filter((it) => it.mode === 'both' || (it.mode === 'pro' && !isSimple)),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <aside
      className="sidebar-surface w-64 flex-shrink-0 flex flex-col h-screen md:sticky top-0 overflow-y-auto safe-top"
      style={{
        // Subtle vertical gradient — adds depth without going theatrical
        background: 'linear-gradient(180deg, #06111F 0%, #0A1A2E 40%, #0A1A2E 100%)',
      }}
    >
      {/* ─── BRAND BLOCK ────────────────────────────────────── */}
      {/* No ring / container around the logo — it now sits cleanly on
          the navy background. The full-color Netcom mark is its own
          shape and a frame around it reads as a placeholder box. */}
      <div className="px-5 pt-5 pb-4 border-b border-white/[0.08] flex-shrink-0">
        <div className="flex items-center gap-3">
          <img
            src="/netcom-logo.png"
            alt="Netcom"
            className="w-10 h-10 object-contain flex-shrink-0"
            style={{ imageRendering: 'auto' }}
          />
          <div className="min-w-0">
            <div className="display text-[16px] font-semibold text-white tracking-tight leading-tight truncate">{BRAND.name}</div>
            <div className="text-[11px] tracking-normal text-white/45 font-semibold mt-1">
              {isSimple ? 'Simple console' : 'Enterprise portal'}
            </div>
          </div>
        </div>
      </div>

      {/* ─── NAV ─────────────────────────────────────────────── */}
      <nav className="flex-1 py-4">
        {filteredSections.map((section) => {
          const accent = section.products && PRODUCT_ACCENTS[section.products[0]];
          return (
            <div key={section.label} className="mb-5">
              {/* Section label */}
              <div className="px-5 mb-2 flex items-center gap-2">
                {accent && (
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: accent.solid, boxShadow: `0 0 6px ${accent.solid}` }}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={`font-semibold ${
                    accent
                      ? 'text-[11px] tracking-normal text-white/75'
                      : 'text-[9px] tracking-normal text-white/35'
                  }`}
                >
                  {section.label}
                </span>
                <div className="flex-1 h-px bg-white/[0.05]" />
              </div>

              {/* Nav items */}
              <ul className="space-y-px px-2">
                {section.items.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={item.demoAction === 'open' ? handleDemoOpen : onNavigate}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-150 ${
                          isActive && !item.demoAction ? 'active' : ''
                        }`
                      }
                      style={({ isActive }) =>
                        isActive && accent && !item.demoAction
                          ? {
                              background: 'rgba(255,255,255,0.07)',
                              color: '#fff',
                              boxShadow: `inset 3px 0 0 ${accent.solid}, 0 0 0 1px rgba(255,255,255,0.04)`,
                            }
                          : undefined
                      }
                    >
                      <item.icon
                        className="w-[18px] h-[18px] flex-shrink-0"
                        style={accent ? { color: accent.solid } : undefined}
                      />
                      <span className="truncate">{item.label}</span>
                      {item.demoAction === 'open' && (
                        <span className="ml-auto text-[9px] font-bold tracking-normal text-sky-300/80">Sandbox</span>
                      )}
                      {item.badgeKey && badges[item.badgeKey] && (
                        <span className="ml-auto text-[10px] font-semibold tabular tracking-normal text-white/50 bg-white/[0.06] rounded-full px-1.5 py-0.5">
                          {badges[item.badgeKey]}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* ─── FOOTER · compliance + version ──────────────────── */}
      <div className="px-5 py-4 border-t border-white/[0.08] flex-shrink-0">
        <div className="text-[11px] tracking-normal text-white/70 font-semibold">Netcom</div>
        <div className="text-[9px] tracking-normal text-white/30 mt-1 leading-snug font-semibold">
          {NETCOM_LOCKUP}
        </div>
      </div>
    </aside>
  );
}

// ─── Inline icons — uniform 18px stroke, slightly heavier weight ─────
function IconHome(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 8l7-5 7 5v9a1 1 0 01-1 1h-4v-6H8v6H4a1 1 0 01-1-1V8z" strokeLinecap="round" strokeLinejoin="round" /></svg>); }
function IconMap(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M7 3L2 5v12l5-2 6 2 5-2V3l-5 2-6-2z" strokeLinejoin="round" /><path d="M7 3v12M13 5v12" /></svg>); }
function IconDevice(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="14" height="10" rx="1.5" /><path d="M7 17h6M10 14v3" strokeLinecap="round" /></svg>); }
function IconSim(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M5 3h7l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" strokeLinejoin="round" /><rect x="7" y="10" width="6" height="5" rx="0.8" /><path d="M9 10v5M11 10v5M7 12h6" strokeLinecap="round" /></svg>); }
function IconLink(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M8 12a4 4 0 010-5.66l2-2a4 4 0 015.66 5.66l-1 1M12 8a4 4 0 010 5.66l-2 2a4 4 0 01-5.66-5.66l1-1" strokeLinecap="round" /></svg>); }
function IconFolder(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 6a1 1 0 011-1h4l2 2h6a1 1 0 011 1v7a1 1 0 01-1 1H4a1 1 0 01-1-1V6z" /></svg>); }
function IconBell(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M6 8a4 4 0 018 0v3l1.5 2.5h-11L6 11V8zM8 16a2 2 0 004 0" strokeLinecap="round" strokeLinejoin="round" /></svg>); }
function IconShield(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M10 2l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V5l7-3z" strokeLinejoin="round" /><path d="M7 10l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>); }
function IconChart(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 17V8m5 9V3m5 14v-7m5 7v-4" strokeLinecap="round" /></svg>); }
function IconList(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M6 5h11M6 10h11M6 15h11M3 5h.01M3 10h.01M3 15h.01" strokeLinecap="round" /></svg>); }
function IconCard(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="2" y="5" width="16" height="11" rx="1.5" /><path d="M2 9h16" /></svg>); }
function IconTicket(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 7a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2a2 2 0 000-4V7z" /><path d="M10 5v10" strokeDasharray="2 2" /></svg>); }
function IconHelp(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="10" cy="10" r="7" /><path d="M8 8a2 2 0 014 0c0 1-2 1.5-2 3M10 14h.01" strokeLinecap="round" /></svg>); }
function IconSliders(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 6h6m4 0h4M3 14h4m4 0h6" strokeLinecap="round" /><circle cx="11" cy="6" r="2" /><circle cx="9" cy="14" r="2" /></svg>); }
function IconWrench(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M13.5 3.5a3.5 3.5 0 0 0-3 5.3l-6.7 6.7a1.5 1.5 0 1 0 2.1 2.1l6.7-6.7a3.5 3.5 0 0 0 4-5.6L15 6 13.4 5l1.8-1.6a3.5 3.5 0 0 0-1.7.1Z" strokeLinejoin="round" /></svg>); }
function IconChip(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="4" y="4" width="12" height="12" rx="1" /><path d="M8 8h4v4H8z M2 7v2M2 11v2M18 7v2M18 11v2M7 2h2M11 2h2M7 18h2M11 18h2" strokeLinecap="round" /></svg>); }
function IconDoc(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M4 2h8l4 4v12H4z" strokeLinejoin="round" /><path d="M12 2v4h4M7 10h6M7 14h6" strokeLinecap="round" /></svg>); }
function IconTeam(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="7" cy="7" r="3" /><path d="M2 17a5 5 0 0110 0M13 5a3 3 0 010 6M18 17a4 4 0 00-5-3.87" strokeLinecap="round" /></svg>); }
function IconGauge(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 13a7 7 0 0114 0M10 13l4-4" strokeLinecap="round" /><circle cx="10" cy="13" r="1.2" fill="currentColor" /></svg>); }
function IconSignal(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M2 14a8 8 0 0116 0" strokeLinecap="round" /><path d="M5 14a5 5 0 0110 0" strokeLinecap="round" /><path d="M8 14a2 2 0 014 0" strokeLinecap="round" /></svg>); }
function IconCode(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m7 6-4 4 4 4M13 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" /></svg>); }
function IconSparkles(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M10 2v3m0 10v3M2 10h3m10 0h3M5 5l2 2m6 6 2 2M5 15l2-2m6-6 2-2" strokeLinecap="round" /></svg>); }
function IconPlus(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M10 4v12m-6-6h12" strokeLinecap="round" /></svg>); }
function IconGrid(props) { return (<svg {...props} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="6" height="6" rx="1" /><rect x="11" y="3" width="6" height="6" rx="1" /><rect x="3" y="11" width="6" height="6" rx="1" /><rect x="11" y="11" width="6" height="6" rx="1" /></svg>); }
